import { useQuery } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useState } from "react";
import uuid from "uuid";

import { getAlignmentsSafely } from "@examspark/api";
import {
  assertNativeError,
  delayForIndexing,
  exists,
  getDateTimestamp,
  truthy,
} from "@examspark/common";
import {
  CreateResourceInput,
  ParsedExam,
  ResourceMaturity,
  ResourceVisibilityType,
  SaveResult,
  UpdateResourceInput,
} from "@examspark/types";

import {
  useMutationCreateExam,
  useMutationUpdateExam,
  useQueryGetPlayableExam,
} from "../../../../api/operations/exam";
import {
  getObjectiveAuthorityIdsFromAppState,
  useAppContext,
} from "../../../../providers/AppContext";
import {
  ExamEditorContext,
  ExamEditorState,
} from "../../../exam-editor/examEditor";
import { ActivityEditorConfig } from "../ActivityEditor";

export type ExamBuilderBeforeSaveParams =
  | { type: "create"; data: CreateResourceInput }
  | { type: "update"; data: UpdateResourceInput };

export type ExamSaveCallback = (
  params: ExamBuilderBeforeSaveParams,
) => unknown | Promise<unknown>;

export type ExamBuilderProps = {
  examId?: string;
  /** Callback to perform some async logic before the exam is saved. */
  onBeforeSave?: ExamSaveCallback;
  config?: ActivityEditorConfig;
};

/**
 * Used by the ExamBuilder component, the wizard-like interface for pulling a
 * desirable set of items based on user input.
 */
export const useExamBuilder = (props: ExamBuilderProps) => {
  const [newActivityState, newActivityDispatch] = useContext(ExamEditorContext);
  const [appState] = useAppContext();
  const { config } = props;

  const [examId, setExamId] = useState<string | undefined>(props.examId);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const { data: initialData, refetch: refetchInitialData } =
    useQueryGetStateFromExam(examId);
  const updateExamMutation = useMutationUpdateExam();
  const createExamMutation = useMutationCreateExam();
  const objectiveAuthorityIds = getObjectiveAuthorityIdsFromAppState(appState);

  useEffect(() => {
    if (initialData && !isLoaded) {
      newActivityDispatch({
        type: "UPDATE",
        data: initialData,
      });
      setIsLoaded(true);
    }
  }, [initialData, isLoaded, newActivityDispatch]);

  const handleReset = useCallback(() => {
    newActivityDispatch({ type: "RESET" });
    setExamId(props.examId);
    setIsLoaded(false);
    setIsCreating(false);
  }, [newActivityDispatch, props.examId]);

  const handleCreate = async (
    overrides?: Partial<ExamEditorState>,
  ): Promise<SaveResult | undefined> => {
    const workspaceId = appState.workspace?.id;
    const userId = appState.user?.id;
    const bankId =
      appState.appLicenseConfig?.userContentBankId ??
      `${workspaceId}/user-content`;

    if (!appState.appLicenseConfig?.userContentBankId) {
      console.error("User content bank ID missing from app license config.");
    }

    try {
      if (!workspaceId || !userId) throw new Error("Invalid Exam");
      const activityName =
        overrides?.name ||
        newActivityState.name ||
        `New Assessment ${getDateTimestamp()}`;

      const examId = uuid();

      const itemIds = Array.from(
        new Set<string>(newActivityState.questions.map((item) => item.id)),
      );

      const filteredItemAlignments: string[] = [];
      for (const item of newActivityState.questions) {
        for (const alignment of getAlignmentsSafely(item.alignments)) {
          const authorityId = alignment?.scope?.split("#")[0] ?? "";
          if (
            objectiveAuthorityIds?.includes(authorityId) ||
            objectiveAuthorityIds?.length === 0
          ) {
            filteredItemAlignments.push(alignment.id);
          }
        }
      }

      const basicData: Omit<CreateResourceInput, "id" | "sk"> = {
        schemaVersion: "2020-01-01",
        name: activityName,
        description: newActivityState.description,

        // metadata
        grade: [overrides?.grade ?? newActivityState.grade].filter(truthy),
        language: [overrides?.language ?? newActivityState.language].filter(
          truthy,
        ),
        subject: [overrides?.subject ?? newActivityState.subject].filter(
          truthy,
        ),

        // sharing-related settings
        workspaceId,
        bankId,
        maturity: newActivityState.maturity ?? ResourceMaturity.Draft,
        creatorId: userId,
        config: JSON.stringify(newActivityState.config ?? {}),
        visibility:
          props.config?.visibility ?? ResourceVisibilityType.Workspace,
      };

      // todo: move these to something like `additionalTags`
      // instead of having course and unit in state
      const tags: any[] = [];

      if (overrides?.tags) {
        for (const tag of overrides.tags) {
          tags.push({
            key: tag.key,
            type: tag.type,
            value: tag.value,
          });
        }
      }

      if (newActivityState.units?.length) {
        for (const unit of newActivityState.units) {
          tags.push({
            key: "unit-id",
            type: "string",
            value: unit.id,
          });
        }
      }

      if (newActivityState.courses?.length) {
        for (const course of newActivityState.courses) {
          tags.push({
            key: "course-id",
            type: "string",
            value: course.id,
          });
        }
      }

      if (overrides?.objectiveAuthorityIds) {
        for (const objectiveAuthorityId of overrides.objectiveAuthorityIds) {
          tags.push({
            key: "objective-authority-id",
            type: "string",
            value: objectiveAuthorityId,
          });
        }
      }

      if (!itemIds.length) {
        if (
          !window.confirm(
            "You have not selected any questions. Are you sure you want to continue? Click OK to continue or Cancel to go back to select questions.",
          )
        ) {
          return;
        }
      }
      if (
        config?.objectives?.requireSelection &&
        !newActivityState.objectives.length
      ) {
        throw new Error("Select at least one Standard before saving.");
      }
      if (itemIds.length > 100) {
        throw new Error(
          "You may not have more than 100 questions in an assessment.",
        );
      }

      // if they were not prompted to show objectives,
      // we save whatever is on the items
      let objectivesToSave = newActivityState.objectives.map((obj) => obj.id);
      if (!config?.objectives?.show) {
        objectivesToSave = filteredItemAlignments;
      }

      // uniq whatever we are saving
      objectivesToSave = Array.from(new Set(objectivesToSave));

      if (
        config?.objectives?.warnOnNoSelection &&
        !newActivityState.objectives.length
      ) {
        if (
          !window.confirm(
            "You have not selected any learning standards. Are you sure you want to continue? Click OK to continue or Cancel to go back to select standards.",
          )
        ) {
          return;
        }
      }

      const input: CreateResourceInput = {
        ...basicData,
        id: examId,
        sk: "exam#v0",
        maturity: "OPERATIONAL",
        interaction: newActivityState.interactionTypes,
        content: {
          forms: [
            {
              sections: [
                {
                  questionIds: itemIds,
                },
              ],
            },
          ],
        },
        tags: tags,
        questionIds: itemIds,
        alignmentIds: objectivesToSave,
      };

      await props.onBeforeSave?.({ type: "create", data: input });
      await createExamMutation.mutateAsync(input);
      setExamId(examId);

      await delayForIndexing();

      return {
        action: "create",
        id: examId,
      };
    } catch (error) {
      assertNativeError(error);
      alert(error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdate = async (): Promise<SaveResult | undefined> => {
    const itemIds = newActivityState.questions.map((q) => q.id);
    const workspaceId = appState.workspace?.id;
    const creatorId = appState.user?.id;

    setIsCreating(true);
    try {
      if (!examId) throw new Error("Invalid Exam");
      if (!itemIds.length) throw new Error("No questions selected");
      if (itemIds.length > 100) {
        throw new Error(
          "You may not have more than 100 questions in an assessment.",
        );
      }

      const id = examId;
      const sk = `exam#v0`;

      if (!id || !sk || !workspaceId || !creatorId) {
        throw new Error("Invalid exam");
      }

      const input: UpdateResourceInput = {
        id,
        sk,
        name: newActivityState.name,
        description: newActivityState.description,
        // interaction: newActivityState.interactionTypes,
        questionIds: itemIds,
        workspaceId,
        creatorId,
        config: JSON.stringify(newActivityState.config ?? {}),
        subject: [newActivityState.subject ?? ""],
        grade: [newActivityState.grade ?? ""],
        maturity: newActivityState.maturity ?? ResourceMaturity.Draft,
        language: [newActivityState.language ?? ""],
        content: {
          forms: [
            {
              sections: [
                {
                  questionIds: itemIds,
                },
              ],
            },
          ],
        },
      };

      // only touch the alignments if we asked about them
      if (config?.objectives?.show) {
        input.alignmentIds = newActivityState.objectives.map((obj) => obj.id);
      }

      await props.onBeforeSave?.({ type: "update", data: input });
      await updateExamMutation.mutateAsync(input);

      await delayForIndexing();
      await refetchInitialData();

      return {
        action: "update",
        id,
      };
    } catch (error) {
      console.error(error);
      alert(error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSave = async (data?: Partial<ExamEditorState>) => {
    if (!examId) {
      return await handleCreate(data);
    } else {
      return await handleUpdate();
    }
  };

  return {
    isCreating,
    isLoaded,
    handleCreate,
    handleUpdate,
    handleSave,
    handleReset,
  };
};

export const useQueryGetStateFromExam = (examId: string | undefined) => {
  // first, get the state we can pull directly from the exam
  const { data: exam } = useQueryGetPlayableExam({
    sk: `exam#v0`,
    id: examId,
  });

  return useQuery({
    queryKey: ["initial-exam-state", exam],
    enabled: !!exam?.id,
    queryFn: async () => {
      const basicState = getStateFromExam(exam);
      return basicState;
    },
  });
};

export const getStateFromExam = (
  exam: ParsedExam | undefined,
): ExamEditorState | undefined => {
  if (exam == null) return undefined;

  return {
    name: exam?.name ?? "",
    banks: [],
    passagesAvailableCount: 0,
    description: exam?.description ?? "",
    questions: exam?.questions?.filter(exists) ?? [],
    interactionTypes: exam?.interaction?.filter(exists) ?? [],

    // metadata
    subject: exam?.subject?.at(0) ?? "",
    language: exam?.language?.at(0) ?? "",
    grade: exam?.grade?.at(0) ?? "",
    maturity: exam?.maturity ?? ResourceMaturity.Draft,

    config:
      exam?.config && typeof exam.config === "string"
        ? JSON.parse(exam.config)
        : {},

    // we dont plan to change these so we can ignore them for now.
    objectives: getAlignmentsSafely(exam.alignments).map((a) => ({
      id: a.id,
      sk: a.sk,
      type: a.type,
      name: a.name,
      abbreviation: a.abbreviation,
      sequence: a.sequence,
      content: a.content,
      scope: a.scope,
    })),
    passages: [],
  };
};
